"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getSavingsReportForDisplay = exports.getNetIncomeReportForDisplay = exports.getIncomeSpendingReportForDisplay = exports.backtrackAccounts = exports.getOverTimePeriods = exports.getOverTimeData = exports.getReportFilteredTxns = void 0;
// CORE
const immutable_1 = require("immutable");
const reselect_1 = require("reselect");
const re_reselect_1 = require("re-reselect");
const luxon_1 = require("luxon");
const lodash_1 = __importDefault(require("lodash"));
const tags_1 = require("companion-app-components/flux/tags");
const categories_1 = require("companion-app-components/flux/categories");
const accounts_1 = require("companion-app-components/flux/accounts");
const reportsUtils_1 = require("companion-app-components/flux/reports/reportsUtils");
// DATA
const selectors_1 = require("data/transactions/selectors");
const selectors_2 = require("data/accountBalances/selectors");
const helpers_1 = require("components/TransactionRegister/helpers");
const objectToKeyUtils_1 = require("utils/objectToKeyUtils");
const memoizeWithKey_1 = require("utils/memoizeWithKey");
const utils_1 = require("data/transactions/utils");
// LOCAL
const utils_2 = require("./utils");
const categoryUtils_1 = require("./categoryUtils");
// local selector used to filter txns by dateRange (luxon Interval) and a filterObject
exports.getReportFilteredTxns = (0, reselect_1.createSelector)((state) => accounts_1.accountsSelectors.getLoadPending(state) || categories_1.categoriesSelectors.getLoadPending(state) || (0, selectors_1.getLoadPending)(state), (state) => accounts_1.accountsSelectors.getReportableAccountsById(state), 
// @ts-expect-error - FIXME: Expected at least 2 arguments, but got 1.ts(2555)
(state) => (0, selectors_1.getTransactionsByAccountId)(state), (state) => categories_1.categoriesSelectors.getCategoriesById(state), (_, props) => (0, helpers_1.createFilterFromFilterObject)(props.filterObject), (_, props) => props.dateRange, // required
(_, props) => props.reportSpecificFilter, // this is for stuff like spending or income, function that accepts txn, true for keep
(_, props) => props.upcomingTxnsIncluded, (loadPending, reportableAccounts, txnsById, categoriesById, filterFn, dateRange, reportSpecificFilter, upcomingTxnsIncluded = false) => {
    if (loadPending) {
        return null;
    }
    const cacheKey = (0, objectToKeyUtils_1.getOrCreateStringFromObject)('report-transactions', { reportableAccounts, txnsById, filterFn, dateRange, reportSpecificFilter, upcomingTxnsIncluded }, 5, memoizeWithKey_1.memoizeWithKeyRetrieveCache);
    return (0, memoizeWithKey_1.memoizeWithKey)('report-transactions', () => {
        // combine txns from reportable accounts
        const allTxns = (0, immutable_1.List)().withMutations((boi) => {
            reportableAccounts.filter((account) => !accounts_1.accountsUtils.isLoanAccount(account)).forEach((account) => {
                var _a;
                (_a = txnsById.get(account.get('id'))) === null || _a === void 0 ? void 0 : _a.toList().forEach((txn) => boi.push(txn));
            });
        });
        const nonC2RTransactions = allTxns.filter((txn) => !(0, utils_1.isC2RTransaction)(txn, reportableAccounts.get(txn.accountId)));
        const withoutUpcomingTxns = !upcomingTxnsIncluded ? nonC2RTransactions.filter((txn) => !(0, utils_1.isUnAcceptedTxnType)(txn)) : nonC2RTransactions;
        // filter txns out by their date and reportSpecificFilter
        const dateFilteredReportSpecificTxns = dateRange
            ? withoutUpcomingTxns.filter((txn) => (!reportSpecificFilter || reportSpecificFilter(txn, categoriesById)) && dateRange.contains(luxon_1.DateTime.fromISO(txn.postedOn)))
            : withoutUpcomingTxns;
        // filter out txns by the filter object if given
        return filterFn ? filterFn(dateFilteredReportSpecificTxns) : dateFilteredReportSpecificTxns;
    }, cacheKey, 5);
});
const getOverTimeData = (txns, dateRange, timeStep, groupBy, categoriesById, tagsById, reportSpecificFilter, includeAllTxns = false) => {
    var _a;
    // @ts-expect-error - This comparison appears to be unintentional because the types 'keyof DurationLikeObject' and '"none"' have no overlap.ts(2367)
    const isTimeStepNone = timeStep === 'none';
    const periods = (0, utils_2.constructDatePeriods)(dateRange, timeStep)
        .map((period) => (Object.assign(Object.assign(Object.assign({}, period), { total: 0, txns: [] }), (groupBy ? { groups: {} } : {}))));
    let boundaryUnit = 'month';
    if (timeStep === 'days') {
        boundaryUnit = 'day';
    }
    else if (timeStep === 'quarters') {
        boundaryUnit = 'quarter';
    }
    else if (timeStep === 'years') {
        boundaryUnit = 'year';
    }
    else if (isTimeStepNone) {
        boundaryUnit = 'day';
    }
    const startDate = (_a = dateRange.start) === null || _a === void 0 ? void 0 : _a.startOf(boundaryUnit);
    const categoriesMap = categoriesById || (0, immutable_1.Map)();
    const tagsMap = tagsById || (0, immutable_1.Map)();
    const allGroups = {};
    const allTxns = [];
    let total = 0;
    if (!txns) {
        return (0, immutable_1.Map)({
            total,
            allTxns: (0, immutable_1.List)(),
            periods,
            allGroups,
        });
    }
    txns.forEach((txn) => {
        var _a;
        const splitItems = (_a = txn.split) === null || _a === void 0 ? void 0 : _a.items;
        let withSplits = [txn];
        if (Array.isArray(splitItems)) {
            withSplits = splitItems;
        }
        else if (splitItems && 'toArray' in splitItems) {
            withSplits = splitItems.toArray();
        }
        let insertedTxn = false; // to avoid duplicating txns when split
        withSplits.forEach((split) => {
            var _a, _b;
            // purely for checking the split, we merge to check payee and account
            if (!reportSpecificFilter(txn.merge(split).set('split', undefined), categoriesMap)) {
                return;
            }
            const txnDate = luxon_1.DateTime.fromISO(txn.postedOn);
            const txnDateNormalized = txnDate.startOf(boundaryUnit);
            let periodIndex = 0;
            if (!isTimeStepNone) {
                const diff = txnDateNormalized.diff(startDate, timeStep);
                periodIndex = Math.trunc(diff[timeStep]);
            }
            const period = periods.get(periodIndex);
            const strSplitAmount = split.amount.toString();
            if (!dateRange.contains(txnDate) || !period) {
                return;
            }
            if (!txn.isExcludedFromReports || includeAllTxns) { // add to total if it's not excluded
                const amount = parseFloat(strSplitAmount);
                period.total += amount;
                total += amount;
            }
            if (!groupBy && !insertedTxn) { // no grouping just plain
                period.txns.push(txn);
                allTxns.push(txn);
                insertedTxn = true;
            }
            else if (groupBy) {
                const { groupKeys } = (0, utils_2.getGroupingKeys)(txn, split, groupBy, categoriesMap, tagsMap);
                const groupKey = (_a = groupKeys[0]) !== null && _a !== void 0 ? _a : -1;
                if (groupKey && ((_b = period.groups) === null || _b === void 0 ? void 0 : _b[groupKey])) { // group has been created
                    if (!txn.isExcludedFromReports || includeAllTxns) {
                        period.groups[groupKey].total += parseFloat(strSplitAmount);
                    }
                    if (!insertedTxn) {
                        period.groups[groupKey].txns.push(txn);
                        period.txns.push(txn);
                        allTxns.push(txn);
                        insertedTxn = true;
                    }
                }
                else { // group not created yet
                    if (period === null || period === void 0 ? void 0 : period.groups) {
                        period.groups[groupKey] = {
                            txns: [txn],
                            total: !txn.isExcludedFromReports || includeAllTxns ? parseFloat(strSplitAmount) : 0,
                        };
                    }
                    if (!insertedTxn) {
                        allTxns.push(txn);
                        period.txns.push(txn);
                        insertedTxn = true;
                    }
                }
                allGroups[groupKey] = (allGroups[groupKey] || 0) + (!txn.isExcludedFromReports || includeAllTxns ? parseFloat(strSplitAmount) : 0);
            }
        });
    });
    return (0, immutable_1.Map)({
        total,
        allTxns: (0, immutable_1.List)(allTxns),
        periods,
        allGroups,
    });
};
exports.getOverTimeData = getOverTimeData;
const getOverTimePeriods = (txns, dateRange, timeStep, groupBy, categoriesById, tagsById, reportSpecificFilter, includeAllTxns = false, parentCategoryId = '') => {
    var _a, _b;
    // @ts-expect-error - This comparison appears to be unintentional because the types 'keyof DurationLikeObject' and '"none"' have no overlap.ts(2367)
    const isTimeStepNone = timeStep === 'none';
    const periods = (0, utils_2.constructDatePeriods)(dateRange, timeStep)
        .map((period) => (Object.assign(Object.assign(Object.assign({}, period), { total: 0, totalOfCategory: 0, txns: [], categoryIds: [parentCategoryId] }), (groupBy ? { groups: {} } : {}))));
    let boundaryUnit = 'month';
    if (timeStep === 'days') {
        boundaryUnit = 'day';
    }
    else if (timeStep === 'quarters') {
        boundaryUnit = 'quarter';
    }
    else if (timeStep === 'years') {
        boundaryUnit = 'year';
    }
    else if (isTimeStepNone) {
        boundaryUnit = 'day'; // safe normalization unit; diff is skipped anyway
    }
    const startDate = (_a = dateRange.start) === null || _a === void 0 ? void 0 : _a.startOf(boundaryUnit);
    (_b = lodash_1.default.uniqBy(txns.toArray(), 'id')) === null || _b === void 0 ? void 0 : _b.forEach((txn) => {
        var _a, _b;
        const withSplits = ((_b = (_a = txn.split) === null || _a === void 0 ? void 0 : _a.items) === null || _b === void 0 ? void 0 : _b.size) > 0 ? txn.split.items : [txn];
        let insertedTxn = false; // to avoid duplicating txns when split
        withSplits.forEach((split) => {
            var _a, _b, _c;
            // purely for checking the split, we merge to check payee and account
            if (!reportSpecificFilter(txn.merge(split).set('split', undefined), categoriesById)) {
                return;
            }
            const txnDate = luxon_1.DateTime.fromISO(txn.postedOn);
            const txnDateNormalized = txnDate.startOf(boundaryUnit);
            let periodIndex = 0;
            if (!isTimeStepNone) {
                const diff = txnDateNormalized.diff(startDate, timeStep);
                periodIndex = Math.trunc(diff[timeStep]);
            }
            const period = periods.get(periodIndex); // obtain period object
            if (!dateRange.contains(txnDate) || !period) {
                return;
            }
            if (!txn.isExcludedFromReports || includeAllTxns) { // add to total if it's not excluded
                const amount = parseFloat(split.amount);
                period.total += amount;
                // accumulate category-specific total
                const coaId = (_a = split.coa) === null || _a === void 0 ? void 0 : _a.id;
                if (coaId && (coaId === parentCategoryId || (0, utils_2.isInCategoryHierarchy)(coaId, parentCategoryId, categoriesById))) {
                    period.totalOfCategory += amount;
                    if (!period.categoryIds.includes(coaId)) {
                        period.categoryIds.push(coaId);
                    }
                }
            }
            if (!groupBy && !insertedTxn) { // no grouping just plain
                period.txns.push(txn);
                insertedTxn = true;
            }
            else if (groupBy) {
                const { groupKeys } = (0, utils_2.getGroupingKeys)(txn, split, groupBy, categoriesById, tagsById);
                const groupKey = (_b = groupKeys[0]) !== null && _b !== void 0 ? _b : -1;
                if ((_c = period.groups) === null || _c === void 0 ? void 0 : _c[groupKey]) { // group has been created
                    if (!txn.isExcludedFromReports || includeAllTxns) {
                        period.groups[groupKey].total += parseFloat(split.amount);
                    }
                    if (!insertedTxn) {
                        period.groups[groupKey].txns.push(txn);
                        period.txns.push(txn);
                        insertedTxn = true;
                    }
                }
                else { // group not created yet
                    if (period === null || period === void 0 ? void 0 : period.groups) {
                        period.groups[groupKey !== null && groupKey !== void 0 ? groupKey : -1] = {
                            txns: [txn],
                            total: !txn.isExcludedFromReports || includeAllTxns ? parseFloat(split.amount) : 0,
                        };
                    }
                    if (!insertedTxn) {
                        period.txns.push(txn);
                        insertedTxn = true;
                    }
                }
            }
        });
    });
    return periods;
};
exports.getOverTimePeriods = getOverTimePeriods;
const getTotalData = (txns, dateRange, timeStep, groupBy, categoriesById, tagsById, reportSpecificFilter, includeAllTxns = false) => {
    // const start = performance.now();
    let groups = {};
    let allTotal = 0;
    const coaHashTable = Object.create(null);
    const coaTree = [];
    const coaDataMap = {};
    txns.forEach((txn) => {
        var _a, _b;
        // @ts-expect-error - FIXME: Property 'size' does not exist on type 'SplitItemProps[]'.ts(2339)
        const withSplits = ((_b = (_a = txn.split) === null || _a === void 0 ? void 0 : _a.items) === null || _b === void 0 ? void 0 : _b.size) && txn.split.items.size > 0 ? txn.split.items : [txn];
        // we have to keep track of which groups the txn has been added to to avoid duplicating in the same group/subgroup but not different ones
        const groupsWithTxn = [];
        withSplits.forEach((split) => {
            if (!reportSpecificFilter(txn.merge(split).set('split', undefined), categoriesById)) { // purely for checking the split, we merge to check the payee and account
                return;
            }
            let alreadyInAllTotal = false;
            if (groupBy === 'category' || groupBy === 'categories') {
                const groupKeys = (0, utils_2.getGroupingKeysForCoa)(txn, split, groupBy, categoriesById);
                const splitAmount = parseFloat(split.amount);
                allTotal += splitAmount;
                (0, categoryUtils_1.buildCoaTree)(coaHashTable, coaTree, groupKeys, txn, splitAmount);
            }
            else {
                const { groupKeys, subKey, parentCategoryId, subCategoryId, originalKey } = (0, utils_2.getGroupingKeys)(txn, split, groupBy, categoriesById, tagsById);
                groupKeys.forEach((grpKey) => {
                    const groupKey = grpKey !== null && grpKey !== void 0 ? grpKey : '';
                    if (groups[groupKey]) { // group exists
                        if (!groupsWithTxn.includes(groupKey)) {
                            groupsWithTxn.push(groupKey);
                            groups[groupKey].txns.push(txn);
                        }
                        if (!txn.isExcludedFromReports || includeAllTxns) {
                            const splitAmount = parseFloat(split.amount);
                            groups[groupKey].total += splitAmount;
                            allTotal += alreadyInAllTotal ? 0 : splitAmount;
                            alreadyInAllTotal = true;
                        }
                        if (subKey) { // if it has a subKey then put it in its subgroup
                            if (!groups[groupKey].subgroups[subKey]) { // if the subgroup hasn't been created yet
                                groups[groupKey].subgroups[subKey] = {
                                    total: 0,
                                    txns: [],
                                    groupKey: subKey,
                                    categoryId: subCategoryId,
                                    subgroups: {},
                                };
                            }
                            const splitAmount = parseFloat(split.amount);
                            groups[groupKey].subgroups[subKey].total += !txn.isExcludedFromReports || includeAllTxns ? splitAmount : 0;
                            if (!groupsWithTxn.includes(groupKey + subKey)) {
                                groupsWithTxn.push(groupKey + subKey);
                                groups[groupKey].subgroups[subKey].txns.push(txn);
                            }
                        }
                    }
                    else { // group does not exist
                        groups[groupKey] = {
                            groupKey,
                            categoryId: parentCategoryId,
                            txns: [txn],
                            originalKey,
                            total: !txn.isExcludedFromReports || includeAllTxns ? parseFloat(split.amount) : 0,
                            subgroups: subKey ?
                                {
                                    [subKey]: {
                                        total: !txn.isExcludedFromReports || includeAllTxns ? parseFloat(split.amount) : 0,
                                        txns: groupsWithTxn.includes(groupKey + subKey) ? [] : [txn],
                                        groupKey: subKey,
                                        categoryId: subCategoryId,
                                        subgroups: {},
                                    },
                                } : {},
                        };
                        groupsWithTxn.push(groupKey + (subKey || ''));
                        if (!alreadyInAllTotal) {
                            allTotal += !txn.isExcludedFromReports || includeAllTxns ? parseFloat(split.amount) : 0;
                            alreadyInAllTotal = true;
                        }
                    }
                });
            }
        });
    });
    if (groupBy === 'category' || groupBy === 'categories') {
        coaTree.forEach((coaNode) => (0, categoryUtils_1.buildCoaGroupBalance)(coaNode));
        coaTree.forEach((coaNode) => (0, categoryUtils_1.buildCoaGroupTxns)(coaNode));
        (0, categoryUtils_1.buildCoaGroup)(coaTree, coaDataMap);
        groups = coaDataMap;
    }
    else {
        Object.keys(groups).forEach((key) => {
            // @ts-expect-error - Type 'List<CashFlowTransaction>' is missing the following properties from type 'CashFlowTransaction[]': length, fill, copyWithin, flat, and 2 more.ts(2740)
            groups[key].txns = (0, immutable_1.List)(groups[key].txns);
        });
    }
    // console.log(`getTotalData took ${performance.now() - start}ms to run`);
    return (0, immutable_1.Map)({
        caller: 'getTotalData',
        allTxns: (0, immutable_1.List)(txns),
        groups,
        total: allTotal,
    });
};
exports.backtrackAccounts = (0, reselect_1.createSelector)((state) => accounts_1.accountsSelectors.getLoadPending(state) || categories_1.categoriesSelectors.getLoadPending(state) || (0, selectors_1.getLoadPending)(state) || tags_1.tagsSelectors.getLoadPending(state), (state) => accounts_1.accountsSelectors.getReportableAccountsById(state), 
// @ts-expect-error - FIXME: Expected at least 2 arguments, but got 1.ts(2555)
(state) => (0, selectors_1.getTransactionsByAccountId)(state), (state) => (0, selectors_2.getAllBalancesByAccountId)(state), (_, props) => props.accountsById, (_, props) => props.dateRange.toISO(), (_, props) => props.timeStep, (_, props) => props.filterObject, (_, props) => (0, utils_2.getReportSpecificFilterFromReportName)(props.reportName), (loadPending, reportableAccounts, txnsByAccountId, balancesById, accountsById, dateRangeISO, timeStep, filterObject, reportSpecificFilter) => {
    if (loadPending) {
        return null;
    }
    const dateRange = luxon_1.Interval.fromISO(dateRangeISO);
    const accountsToBacktrack = accountsById.filter((_, accountId) => // at this time the only filter allowed for Savings and NetWorth report are accounts
     {
        var _a, _b, _c;
        return reportableAccounts.has(accountId) // account is reportable
            // only one of the following lines has to be true for the accountId to be kept
            && ((!filterObject || ((_a = filterObject.get('accounts')) === null || _a === void 0 ? void 0 : _a.length) < 0) // there's no filter or no filters for accounts
                || (!filterObject.get('filterOut') && ((_b = filterObject.get('accounts')) === null || _b === void 0 ? void 0 : _b.includes(accountId))) // filtering in and it's included in accounts
                || (filterObject.get('filterOut') && !((_c = filterObject.get('accounts')) === null || _c === void 0 ? void 0 : _c.includes(accountId))));
    }); // filtering out and it's not included in accounts
    const balancePeriods = (0, utils_2.constructDatePeriods)(dateRange, timeStep, false);
    let allTxns = (0, immutable_1.List)();
    const retTxnsByAccountId = {};
    accountsToBacktrack.forEach((acc, accountId) => {
        const txns = txnsByAccountId.get(accountId).toList();
        const filterIncludingAppliedFilter = filterObject ? (txn, cbid) => reportSpecificFilter(txn, cbid) && (0, helpers_1.createFilterFromFilterObject)(filterObject)([txn]).length > 0 : reportSpecificFilter;
        // we calculate the data up until today so we can backtrack correctly
        const overTimeData = (0, exports.getOverTimeData)(txns, (0, reportsUtils_1.adjustDateRangeEndToToday)(dateRange), timeStep, null, null, null, filterIncludingAppliedFilter, true);
        // but then we remove the txns outside the range
        const dateCorrectedTxns = overTimeData.get('allTxns').filter((txn) => dateRange.contains(luxon_1.DateTime.fromISO(txn.postedOn)));
        allTxns = allTxns.concat(dateCorrectedTxns);
        retTxnsByAccountId[acc.name] = dateCorrectedTxns;
        let runningBalance = balancesById.get(accountId).currentBalance;
        let periodIndex = balancePeriods.size - 1;
        overTimeData.get('periods').reverse().forEach((period) => {
            if (dateRange.contains(luxon_1.DateTime.fromISO(period.timeStamp))) {
                let notIncludedTotal = 0;
                period.txns.forEach((txn) => {
                    if (!dateRange.contains(luxon_1.DateTime.fromISO(txn.postedOn))) {
                        notIncludedTotal += Number(txn.amount);
                    }
                });
                const balancePeriod = balancePeriods.get(periodIndex);
                if (balancePeriod) {
                    balancePeriod[acc.name] = runningBalance - notIncludedTotal;
                    balancePeriod.total += runningBalance - notIncludedTotal;
                }
                periodIndex -= 1;
            }
            runningBalance -= period.total;
        });
    });
    return (0, immutable_1.Map)({ periods: balancePeriods, txnsByAccountId: retTxnsByAccountId, allTxns });
});
// ================================================================================================================================== //
//                                                      For Display Selectors                                                         //
// ================================================================================================================================== //
exports.getIncomeSpendingReportForDisplay = (0, re_reselect_1.createCachedSelector)((state) => accounts_1.accountsSelectors.getLoadPending(state) || categories_1.categoriesSelectors.getLoadPending(state) || (0, selectors_1.getLoadPending)(state) || tags_1.tagsSelectors.getLoadPending(state), (state, props) => (0, exports.getReportFilteredTxns)(state, Object.assign(Object.assign({}, props), { reportSpecificFilter: (0, utils_2.getReportSpecificFilterFromReportName)(props.reportName) })), (state) => categories_1.categoriesSelectors.getCategoriesById(state), (state) => tags_1.tagsSelectors.getTags(state), (_, props) => props.reportName, (_, props) => props.dateRange, (_, props) => props.timeStep, (_, props) => props.isOverTime, (_, props) => props.chartType, (_, props) => props.groupBy, (_, props) => (0, helpers_1.createFilterFromFilterObject)(props.filterObject), (_, props) => (0, utils_2.getReportSpecificFilterFromReportName)(props.reportName), (loadPending, txnsList, categoriesById, tagsById, reportName, dateRange, timeStep, isOverTime, chartType, groupBy, filterObjectFn, reportSpecificFilter) => {
    if (loadPending || !txnsList) {
        return null;
    }
    const retData = { allTxns: null, chartData: {} };
    const filterIncludingAppliedFilters = filterObjectFn
        ? (txn, cbid) => { var _a; return reportSpecificFilter(txn, cbid) && ((_a = filterObjectFn((0, immutable_1.List)([txn]))) === null || _a === void 0 ? void 0 : _a.size) > 0; }
        : reportSpecificFilter;
    const report = (isOverTime ? exports.getOverTimeData : getTotalData)(txnsList, dateRange, timeStep, groupBy, categoriesById, tagsById, filterIncludingAppliedFilters, true);
    if (!report)
        return null;
    retData.allTxns = report.get('allTxns');
    if (reportName === 'spending') {
        const spendingReport = (getTotalData)(txnsList, dateRange, timeStep, groupBy, categoriesById, tagsById, filterIncludingAppliedFilters, true);
        if (spendingReport) {
            const convertedGroups = (0, utils_2.convertAndSortGrouping)((spendingReport === null || spendingReport === void 0 ? void 0 : spendingReport.get('groups')) || {}, -1);
            const [cappedGroups, allTxns, allTotal] = (0, utils_2.capGrouping)(convertedGroups, 9, 'Everything else');
            retData.chartData.spendings = [{
                    groupKey: `All ${(0, reportsUtils_1.makeGroupByPlural)(groupBy || '')}`,
                    subgroups: cappedGroups,
                    total: allTotal,
                    txns: allTxns,
                }];
        }
    }
    if (chartType === 'bar' || chartType === 'stacked bar' || chartType === 'grouped bar') {
        const signMultiple = reportName === 'spending' ? -1 : 1;
        const allGroupsEntries = Object.entries(report.get('allGroups') || {});
        retData.chartData.orderedFields = allGroupsEntries
            .map(([groupKey, value]) => ({ label: groupKey, total: value }))
            .sort((leftEntry, rightEntry) => (signMultiple * rightEntry.total) - (signMultiple * leftEntry.total))
            .map(({ label }) => label);
        if (retData.chartData.orderedFields.length > 4) {
            retData.chartData.orderedFields = retData.chartData.orderedFields.slice(0, 4).concat(['Everything else']);
        }
        const allZeroGroups = {}; // we need to zero out all the groups for each period for "consistency"
        retData.chartData.orderedFields.forEach((groupKey) => { allZeroGroups[groupKey] = 0; });
        retData.total = report.get('total');
        const barPeriods = report === null || report === void 0 ? void 0 : report.get('periods');
        const chartPeriods = barPeriods === null || barPeriods === void 0 ? void 0 : barPeriods.map((period) => {
            const groupObject = period.groups ? { txns: (0, immutable_1.List)(period.txns) } : { total: period.total, txns: (0, immutable_1.List)(period.txns) };
            if (period.groups) { // handle groups of data
                Object.entries(period.groups).forEach(([groupKey, groupData]) => {
                    var _a;
                    const cappedKey = ((_a = retData.chartData.orderedFields) === null || _a === void 0 ? void 0 : _a.includes(groupKey)) ? groupKey : 'Everything else';
                    groupObject[cappedKey] = (groupObject[cappedKey] || 0) + (signMultiple * groupData.total);
                });
            }
            return Object.assign(Object.assign({ timeStamp: period.timeStamp }, allZeroGroups), groupObject);
        }).toArray();
        retData.chartData.periods = chartPeriods;
    }
    else if (chartType === 'sideways bar' || chartType === 'pie') {
        const convertedGroups = (0, utils_2.convertAndSortGrouping)((report === null || report === void 0 ? void 0 : report.get('groups')) || {}, reportName === 'spending' ? -1 : 1);
        const [cappedGroups, allTxns, allTotal] = (0, utils_2.capGrouping)(convertedGroups, 9, 'Everything else');
        retData.chartData.totals = [{
                groupKey: `All ${(0, reportsUtils_1.makeGroupByPlural)(groupBy || '')}`,
                subgroups: cappedGroups,
                total: allTotal,
                txns: allTxns,
            }];
        retData.chartData.orderedFields = cappedGroups.map((g) => (g === null || g === void 0 ? void 0 : g.groupKey) || '');
        retData.total = report.get('total');
    }
    return (0, immutable_1.Map)(Object.assign(Object.assign({}, retData), { rawReport: report }));
})({
    // this line sets the key to use for the cache
    keySelector: (state, { dateRange, reportName, chartType, isOverTime }) => `${dateRange ? dateRange.toString() : 'default'}_${reportName || ''}_${chartType}_${isOverTime ? 'overtime' : 'total'}`,
    cacheObject: new re_reselect_1.LruObjectCache({ cacheSize: 6 }),
});
exports.getNetIncomeReportForDisplay = (0, re_reselect_1.createCachedSelector)((state) => accounts_1.accountsSelectors.getLoadPending(state) || categories_1.categoriesSelectors.getLoadPending(state) || (0, selectors_1.getLoadPending)(state) || tags_1.tagsSelectors.getLoadPending(state), (state, props) => (0, exports.getReportFilteredTxns)(state, Object.assign(Object.assign({}, props), { reportSpecificFilter: (0, utils_2.getReportSpecificFilterFromReportName)('spending') })), (state, props) => (0, exports.getReportFilteredTxns)(state, Object.assign(Object.assign({}, props), { reportSpecificFilter: (0, utils_2.getReportSpecificFilterFromReportName)('income') })), (state) => categories_1.categoriesSelectors.getCategoriesById(state), (state) => tags_1.tagsSelectors.getTags(state), (_, props) => props.reportName, (_, props) => props.dateRange, (_, props) => props.timeStep, (_, props) => props.isOverTime, (_, props) => props.chartType, (_, props) => props.groupBy, (_, props) => (0, helpers_1.createFilterFromFilterObject)(props.filterObject), (loadPending, spendingTxns, incomeTxns, categoriesById, tagsById, reportName, dateRange, timeStep, isOverTime, chartType, groupBy, filterObjectFn) => {
    if (loadPending) {
        return null;
    }
    const spendingFilter = (0, utils_2.getReportSpecificFilterFromReportName)('spending');
    const spendingFilterIncludingAppliedFilters = filterObjectFn
        ? (txn, cbid) => { var _a; return spendingFilter(txn, cbid) && ((_a = filterObjectFn((0, immutable_1.List)([txn]))) === null || _a === void 0 ? void 0 : _a.size) > 0; }
        : spendingFilter;
    const spendingReport = (isOverTime ? exports.getOverTimeData : getTotalData)(spendingTxns, dateRange, timeStep, groupBy, categoriesById, tagsById, spendingFilterIncludingAppliedFilters, true);
    const incomeFilter = (0, utils_2.getReportSpecificFilterFromReportName)('income');
    const incomeFilterIncludingAppliedFilters = filterObjectFn
        ? (txn, cbid) => { var _a; return incomeFilter(txn, cbid) && ((_a = filterObjectFn((0, immutable_1.List)([txn]))) === null || _a === void 0 ? void 0 : _a.size) > 0; }
        : incomeFilter;
    const incomeReport = (isOverTime ? exports.getOverTimeData : getTotalData)(incomeTxns, dateRange, timeStep, groupBy, categoriesById, tagsById, incomeFilterIncludingAppliedFilters, true);
    const retData = { allTxns: null, chartData: {} };
    retData.allTxns = (0, immutable_1.List)(lodash_1.default.unionBy(spendingReport.get('allTxns').toArray(), incomeReport.get('allTxns').toArray(), 'id'));
    const spendingPeriods = spendingReport.get('periods');
    const incomePeriods = incomeReport.get('periods');
    retData.chartData.periods = (spendingPeriods === null || spendingPeriods === void 0 ? void 0 : spendingPeriods.toArray().map((period, index) => {
        const spendingTotal = period.total;
        const incomeTotal = incomePeriods.get(index).total;
        const combinedTxns = period.txns.concat(incomePeriods.get(index).txns);
        const uniqueCombinedTxns = (0, immutable_1.List)(lodash_1.default.uniqBy(combinedTxns, (e) => e.id)); // eliminate duplicate txns
        return {
            txns: uniqueCombinedTxns || (0, immutable_1.List)(),
            spendingTotal: Math.abs(spendingTotal),
            spendingTotalForReport: spendingTotal,
            incomeTotal: Math.abs(incomeTotal),
            incomeTotalForReport: incomeTotal,
            netIncomeTotal: incomeTotal + spendingTotal,
            timeStamp: period.timeStamp,
        };
    })) || [];
    retData.total = incomeReport.get('total') + spendingReport.get('total');
    const spendingGroupByCategory = getTotalData(spendingTxns, dateRange, timeStep, 'category', categoriesById, tagsById, spendingFilterIncludingAppliedFilters, true);
    if (spendingGroupByCategory) {
        const convertedGroups = (0, utils_2.convertAndSortGroupingByKey)((spendingGroupByCategory === null || spendingGroupByCategory === void 0 ? void 0 : spendingGroupByCategory.get('groups')) || {}, -1);
        const [cappedGroups, allTxns, allTotal] = (0, utils_2.capGrouping)(convertedGroups, 9, 'Everything else');
        const nestedExpenseData = [{ groupkey: 'Total Expense', subgroups: cappedGroups, total: allTotal, txns: allTxns }];
        const [, ...flattenedExpenses] = (0, utils_2.flattenNestedGroupings)(nestedExpenseData);
        retData.expenses = flattenedExpenses.map((objCategory) => (Object.assign(Object.assign({}, objCategory), { periods: (0, exports.getOverTimePeriods)(objCategory.txns, dateRange, timeStep, groupBy, categoriesById, tagsById, spendingFilterIncludingAppliedFilters, true, objCategory === null || objCategory === void 0 ? void 0 : objCategory.categoryId) || [] })));
    }
    const incomeGroupByCategory = getTotalData(incomeTxns, dateRange, timeStep, 'category', categoriesById, tagsById, incomeFilterIncludingAppliedFilters, true);
    if (incomeGroupByCategory) {
        const convertedGroups = (0, utils_2.convertAndSortGroupingByKey)((incomeGroupByCategory === null || incomeGroupByCategory === void 0 ? void 0 : incomeGroupByCategory.get('groups')) || {}, 1);
        const [cappedGroups, allTxns, allTotal] = (0, utils_2.capGrouping)(convertedGroups, 9, 'Everything else');
        const nestedIncomeData = [{ groupkey: 'Total Income', subgroups: cappedGroups, total: allTotal, txns: allTxns }];
        const [, ...flattenedIncomes] = (0, utils_2.flattenNestedGroupings)(nestedIncomeData);
        retData.incomes = flattenedIncomes.map((objCategory) => (Object.assign(Object.assign({}, objCategory), { periods: (0, exports.getOverTimePeriods)(objCategory.txns, dateRange, timeStep, groupBy, categoriesById, tagsById, incomeFilterIncludingAppliedFilters, true, objCategory === null || objCategory === void 0 ? void 0 : objCategory.categoryId) || [] })));
    }
    return (0, immutable_1.Map)(Object.assign(Object.assign({}, retData), { rawReport: spendingReport }));
})({
    // this line sets the key to use for the cache
    keySelector: (state, { dateRange, reportName, chartType, isOverTime }) => `${dateRange ? dateRange.toString() : 'default'}_${reportName || ''}_${chartType}_${isOverTime ? 'overtime' : 'total'}`,
    cacheObject: new re_reselect_1.LruObjectCache({ cacheSize: 6 }),
});
exports.getSavingsReportForDisplay = (0, reselect_1.createSelector)((state) => accounts_1.accountsSelectors.getLoadPending(state) || (0, selectors_1.getLoadPending)(state), (state, props) => (0, exports.backtrackAccounts)(state, Object.assign(Object.assign({}, props), { accountsById: accounts_1.accountsSelectors.getSavingAccounts(state) })), (_, props) => props.chartType, (_, props) => props.groupBy, (loadPending, report, chartType, groupBy) => {
    var _a;
    if (loadPending || !report) {
        return null;
    }
    const retData = { allTxns: null, chartData: {} };
    retData.chartData.orderedFields = (!groupBy || groupBy === 'none') ? ['total'] : Object.entries(report.get('periods').last()) // destructure to entries list
        .filter(([accountName, _value]) => accountName !== 'total' && accountName !== 'timeStamp') // remove the total and timeStamp properties
        .sort(([_aN1, value1], [_aN2, value2]) => (Number(value2) || 0) - (Number(value1) || 0)) // sort based on final month value
        .map(([accountName, _value]) => accountName); // reduce to just account names
    retData.total = (_a = report.get('periods').last()) === null || _a === void 0 ? void 0 : _a.total;
    retData.allTxns = report.get('allTxns');
    if (chartType === 'sideways bar' || chartType === 'pie') {
        const finalMonth = report.get('periods').last();
        const groups = retData.chartData.orderedFields.map((accountName) => ({
            groupKey: accountName,
            subgroups: null,
            total: finalMonth[accountName],
            txns: groupBy === 'none' ? report.get('allTxns') : report.get('txnsByAccountId')[accountName],
        }));
        // @ts-expect-error -  Type 'null' is not assignable to type 'GroupingNode[] | GroupingMap | undefined'.ts(2345)
        const convertedGroups = (0, utils_2.convertAndSortGrouping)(groups || {}, 1);
        const [cappedGroups, allTxns, allTotal] = (0, utils_2.capGrouping)(convertedGroups, 9, 'Everything else');
        retData.chartData.totals = [{ groupKey: 'All Savings', subgroups: cappedGroups, total: allTotal, txns: allTxns }];
    }
    else {
        retData.chartData.periods = report.get('periods').toArray();
    }
    return (0, immutable_1.Map)(Object.assign(Object.assign({}, retData), { rawReport: report }));
});
